import React, { useRef, useState, useEffect } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import axios from '../api/axiosConfig';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import TermsModal from '../components/modal/TermsModal';


interface IFormData {
  age: number;
  gender: string;
  phonePart1: string;
  phonePart2: string;
  phonePart3: string;
  agreeToTerms: boolean;
  agreeToPrivacyPolicy: boolean;
  agreeToLocation: boolean;
  agreeToSensitiveInfo: boolean;
}

const SocialOnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const [agreements, setAgreements] = useState({
    agreeToAll: false,
    agreeToTerms: false,
    agreeToPrivacyPolicy: false,
    agreeToLocation: false,
    agreeToSensitiveInfo: false,
  });

  const [modal, setModal] = useState({ isOpen: false, title: '', content: '' });

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    trigger,
    watch,
  } = useForm<IFormData>({ mode: 'onChange' });

  const { checkSubmissionStatus, user, setUser } = useAuth() as any;

  const phonePart1Ref = useRef<HTMLInputElement>(null);
  const phonePart2Ref = useRef<HTMLInputElement>(null);
  const phonePart3Ref = useRef<HTMLInputElement>(null);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let filteredValue = value.replace(/[^0-9]/g, '');
    if (name === 'phonePart1') {
      filteredValue = filteredValue.slice(0, 3);
    } else if (name === 'phonePart2' || name === 'phonePart3') {
      filteredValue = filteredValue.slice(0, 4);
    }

    setValue(name as keyof IFormData, filteredValue as any);
    trigger(name as keyof IFormData);

    if (name === 'phonePart1' && filteredValue.length === 3) {
      phonePart2Ref.current?.focus();
    } else if (name === 'phonePart2' && filteredValue.length === 4) {
      phonePart3Ref.current?.focus();
    }
  };

  const handlePhoneKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const { name, value, selectionStart } = e.currentTarget;

    if (e.key === 'Backspace' && selectionStart === 0 && value === '') {
      e.preventDefault();

      if (name === 'phonePart3') {
        phonePart2Ref.current?.focus();
      } else if (name === 'phonePart2') {
        phonePart1Ref.current?.focus();
      }
    }
  };

  const handleAgreementChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    if (name === 'agreeToAll') {
      setAgreements({
        agreeToAll: checked,
        agreeToTerms: checked,
        agreeToPrivacyPolicy: checked,
        agreeToLocation: checked,
        agreeToSensitiveInfo: checked,
      });
    } else {
      const newAgreements = { ...agreements, [name]: checked };
      const allChecked =
        newAgreements.agreeToTerms &&
        newAgreements.agreeToPrivacyPolicy &&
        newAgreements.agreeToLocation &&
        newAgreements.agreeToSensitiveInfo;
      setAgreements({ ...newAgreements, agreeToAll: allChecked });
    }
  };

  const allAgreed =
    agreements.agreeToTerms &&
    agreements.agreeToPrivacyPolicy &&
    agreements.agreeToLocation &&
    agreements.agreeToSensitiveInfo;

  const showTerms = async (type: string) => {
    try {
      const response = await axios.get('/api/terms');
      const terms = response.data[type];
      const contentWithBreaks = terms.content.replace(/\n/g, '<br />');
      setModal({ isOpen: true, title: terms.title, content: contentWithBreaks });
    } catch (error) {
      console.error('Failed to fetch terms', error);
    }
  };

  const onSubmit: SubmitHandler<IFormData> = async data => {
    if (!allAgreed) {
      alert('모든 약관에 동의해야 서비스를 이용할 수 있습니다.');
      return;
    }

    try {
      const fullPhoneNumber = `${data.phonePart1}-${data.phonePart2}-${data.phonePart3}`;

      await axios.post('/api/users/update-additional-info', {
        ...data,
        phoneNumber: fullPhoneNumber,
        ...agreements,
      });

      if (typeof setUser === 'function') {
        setUser((prev: any) => (prev ? { ...prev, hasProvidedAdditionalInfo: true } : prev));
      }
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const u = JSON.parse(userStr);
        u.hasProvidedAdditionalInfo = true;
        localStorage.setItem('user', JSON.stringify(u));
      }

      const hasSubmitted = await checkSubmissionStatus().catch(() => false);

      if (hasSubmitted) {
        navigate('/', { replace: true, state: { completedOnboarding: true } });
      } else {
        navigate('/daily-journal', { replace: true, state: { completedOnboarding: true } });
      }
    } catch (error) {
      console.error('Failed to submit additional info:', error);
      alert('정보 제출에 실패했습니다.');
    }
  };

  return (
    <div className="register-page">
      <div className="register-container">
        <h2>추가 정보 입력</h2>
        <p>서비스 이용을 위해 추가 정보를 입력해주세요.</p>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="form-group">
            <label htmlFor="age">나이</label>
            <input
              type="number"
              id="age"
              {...register('age', {
                required: '나이는 필수 항목입니다.',
                valueAsNumber: true,
                min: { value: 1, message: '유효한 나이를 입력해주세요.' },
              })}
            />
            {errors.age && <p className="error-message">{errors.age.message}</p>}
          </div>

          <div className="form-group">
            <label htmlFor="gender">성별</label>
            <select
              id="gender"
              {...register('gender', { required: '성별은 필수 항목입니다.' })}
              defaultValue=""
            >
              <option value="" disabled>
                선택
              </option>
              <option value="male">남</option>
              <option value="female">여</option>
              <option value="other">기타</option>
            </select>
            {errors.gender && <p className="error-message">{errors.gender.message}</p>}
          </div>

          <div className="form-group">
            <label htmlFor="phoneNumber">휴대폰 번호</label>
            <div className="phone-input-group">
              <input
                type="text"
                id="phonePart1"
                maxLength={3}
                {...register('phonePart1', {
                  required: '휴대폰 번호는 필수 항목입니다.',
                  maxLength: 3,
                  minLength: 3,
                })}
                onChange={handlePhoneChange}
                onKeyDown={handlePhoneKeyDown}
                ref={phonePart1Ref}
              />
              <span>-</span>
              <input
                type="text"
                id="phonePart2"
                maxLength={4}
                {...register('phonePart2', {
                  required: '휴대폰 번호는 필수 항목입니다.',
                  maxLength: 4,
                  minLength: 4,
                })}
                onChange={handlePhoneChange}
                onKeyDown={handlePhoneKeyDown}
                ref={phonePart2Ref}
              />
              <span>-</span>
              <input
                type="text"
                id="phonePart3"
                maxLength={4}
                {...register('phonePart3', {
                  required: '휴대폰 번호는 필수 항목입니다.',
                  maxLength: 4,
                  minLength: 4,
                })}
                onChange={handlePhoneChange}
                onKeyDown={handlePhoneKeyDown}
                ref={phonePart3Ref}
              />
            </div>
            {(errors.phonePart1 || errors.phonePart2 || errors.phonePart3) && (
              <p className="error-message">휴대폰 번호는 필수 항목입니다</p>
            )}
          </div>

          <div className="form-group agreements">
            <div className="agreement-item">
              <input
                type="checkbox"
                id="agreeToAll"
                name="agreeToAll"
                checked={agreements.agreeToAll}
                onChange={handleAgreementChange}
              />
              <label htmlFor="agreeToAll">전체 동의</label>
            </div>
            <hr />
            <div className="agreement-item">
              <input
                type="checkbox"
                id="agreeToTerms"
                name="agreeToTerms"
                checked={agreements.agreeToTerms}
                onChange={handleAgreementChange}
              />
              <label htmlFor="agreeToTerms">
                <span onClick={() => showTerms('terms')}>서비스 이용약관 (필수)</span>
              </label>
            </div>
            <div className="agreement-item">
              <input
                type="checkbox"
                id="agreeToPrivacyPolicy"
                name="agreeToPrivacyPolicy"
                checked={agreements.agreeToPrivacyPolicy}
                onChange={handleAgreementChange}
              />
              <label htmlFor="agreeToPrivacyPolicy">
                <span onClick={() => showTerms('privacy')}>개인정보처리방침 (필수)</span>
              </label>
            </div>
            <div className="agreement-item">
              <input
                type="checkbox"
                id="agreeToLocation"
                name="agreeToLocation"
                checked={agreements.agreeToLocation}
                onChange={handleAgreementChange}
              />
              <label htmlFor="agreeToLocation">
                <span onClick={() => showTerms('location')}>위치기반서비스 이용약관 (필수)</span>
              </label>
            </div>
            <div className="agreement-item">
              <input
                type="checkbox"
                id="agreeToSensitiveInfo"
                name="agreeToSensitiveInfo"
                checked={agreements.agreeToSensitiveInfo}
                onChange={handleAgreementChange}
              />
              <label htmlFor="agreeToSensitiveInfo">
                <span onClick={() => showTerms('sensitive')}>민감정보 처리 동의서 (필수)</span>
              </label>
            </div>
          </div>

          <button type="submit" className="submit-button" disabled={!isValid || !allAgreed}>
            작성 완료
          </button>
        </form>
      </div>
      {modal.isOpen && (
        <TermsModal
          title={modal.title}
          content={modal.content}
          onClose={() => setModal({ isOpen: false, title: '', content: '' })}
        />
      )}
    </div>
  );
};

export default SocialOnboardingPage;
